import { api } from "~/utils/api"
import SuperChart, { type DateChartSeries } from "./SuperChart"
import { useSkuStore } from "~/providers/sku-store-provider"
import LoadingOverlay from "../LoadingOverlay"
import { useMemo } from "react"
import { subYears } from "date-fns"
import { startOfTodayUTC } from "~/utils/datetime"
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../Card"
import { ChartType } from "~/constants/graphs"
import { useChartSeriesConfig } from "~/constants/colors"

type Props = {
  variantIds: string[]
}

export default function ReturnsGraph({ variantIds }: Props) {
  const chartConfig = useChartSeriesConfig()
  const startDate = subYears(startOfTodayUTC(), 1)
  const endDate = startOfTodayUTC()
  const warehouseId = useSkuStore((state) => state.warehouseId)

  const { data, isLoading } = api.product.returns.useQuery({
    variantIds,
    warehouseId,
    startDate,
    endDate,
  })

  const series: DateChartSeries = useMemo(
    () => ({
      data:
        data?.returnsWithCurrentWeek.map((entry) => ({
          milliseconds: new Date(entry.date).getTime(),
          value: entry.value,
        })) ?? [],
      name: "Returns",
      type: "column",
      colors: [chartConfig.expectedReturns.color],
    }),
    [data, chartConfig],
  )

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Returns</CardTitle>
        <CardDescription>Last 52 weeks</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingOverlay />
        ) : (
          <SuperChart
            type={ChartType.TimeSeries}
            data={[series]}
            dateRange={{ start: startDate, end: endDate }}
          />
        )}
      </CardContent>
    </Card>
  )
}
